import {ChangeDetectorRef, DestroyRef, Directive, HostBinding, inject, Input, OnInit} from '@angular/core';
import {
  ApplicationConfigService,
  CirculationView,
  CodebookService,
  DocumentDtoWithAuthorization,
  DocumentFileDetailService,
  doneActivityStates,
  doneTaskResolutions,
  EsslApplicationModulesService,
  ExtendedCirculationActivityDto,
  ExtendedCirculationTaskDto,
  FileDtoWithAuthorization,
  getBigObjectIcon,
  getRegistryTransferLabel,
  getRegistryTransferTaskTagColor,
  inProgressActivityStates,
  inProgressTaskResolutions,
  isDocumentEntity
} from '|shared';
import {ApiSharedFolderService, DocumentDto} from '|api/document';
import {of, switchMap} from 'rxjs';
import {EsslApplicationModule, EsslObjectType, IssdDto} from '|api/commons';
import {map} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiUserCategoryAssignmentService, UserCategoryAssignmentDto, UserCategoryDto} from '|api/core';

@Directive()
export abstract class DocumentFileDetailToolbarComponent<TObject extends DocumentDtoWithAuthorization | FileDtoWithAuthorization> implements OnInit {

  private cd = inject(ChangeDetectorRef);
  protected destroyRef = inject(DestroyRef);
  private codebookService = inject(CodebookService);
  private apiSharedFolderService = inject(ApiSharedFolderService);
  private documentFileDetailService = inject(DocumentFileDetailService);
  private esslApplicationModulesService = inject(EsslApplicationModulesService);
  private apiUserCategoryAssignmentService = inject(ApiUserCategoryAssignmentService);
  protected applicationConfigService = inject(ApplicationConfigService);

  @Input({required: true})
  runningActivity: Nullable<ExtendedCirculationActivityDto>;
  @Input({required: true})
  isRegistryOfficeTransferActivityView!: boolean;
  @Input({required: true})
  refActivity: Nullable<ExtendedCirculationActivityDto>;
  @Input({required: true})
  refTask: Nullable<ExtendedCirculationTaskDto>;

  protected object!: TObject;
  protected userCategoryAssignment: Nullable<UserCategoryAssignmentDto>;

  private issdApplications: IssdDto[] = [];

  isSharedInAccessibleFolder$ = this.documentFileDetailService.object$.pipe(
    switchMap(object => {
      if (this.esslApplicationModulesService.isEsslApplicationModuleActive(EsslApplicationModule.SHARED_FOLDER)) {
        return this.apiSharedFolderService.sharedFolderGetAllAccessibleFolders().pipe(map(
          accessibleFolders => accessibleFolders.some(
            f => (object.sharedFolderIds ?? []).includes(f.id)
          )
        ));
      }
      else {
        return of(false);
      }
    })
  );

  @HostBinding('class.in-issd-application')
  get isIssdActivity() {
    return this.object?.issdId;
  }

  get isOnInProgressTaskView() {
    return this.refTask && inProgressTaskResolutions.includes(this.refTask.resolution);
  }

  get isOnDoneTaskView() {
    return this.refTask && doneTaskResolutions.includes(this.refTask.resolution);
  }

  get isOnInProgressActivityView() {
    return this.refActivity && inProgressActivityStates.includes(this.refActivity.activityState);
  }

  get isOnDoneActivityView() {
    return this.refActivity && doneActivityStates.includes(this.refActivity.activityState);
  }

  get isInCirculationSolvingMode() {
    return this.isOnInProgressTaskView || this.isOnDoneTaskView || this.isOnInProgressActivityView || this.isOnDoneActivityView;
  }

  get circulationToolbarView(): CirculationView {
    if (this.isOnInProgressTaskView) {
      return CirculationView.OBJECT_DETAIL_IN_PROGRESS_TASK;
    }
    else if (this.isOnDoneTaskView) {
      return CirculationView.OBJECT_DETAIL_DONE_TASK;
    }
    else if (this.isOnInProgressActivityView) {
      return CirculationView.OBJECT_DETAIL_IN_PROGRESS_ACTIVITY;
    }
    else {
      return CirculationView.OBJECT_DETAIL_DONE_ACTIVITY;
    }
  }

  get issdApplication() {
    if (this.isIssdActivity) {
      return this.issdApplications.find(app => app.id === (this.object as DocumentDto)?.issdId);
    }
    else {
      return null;
    }
  }

  openUrl(url: Nullable<string>) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  get isAgendaTransferConfirmed() {
    return this.object?.agendaTransferConfirmed;
  }

  get bigIcon() {
    return getBigObjectIcon(this.object);
  }

  get registryOfficeTransferTagLabel() {
    return getRegistryTransferLabel((this.object as FileDtoWithAuthorization).registryOfficeTransfer?.registryOfficeTransferState);
  }

  get registryOfficeTransferTagColor() {
    return getRegistryTransferTaskTagColor((this.object as FileDtoWithAuthorization).registryOfficeTransfer?.registryOfficeTransferState);
  }

  onUserCategorySelected(category: Nullable<UserCategoryDto>) {
    const entityId = this.object.id!;
    const entityType = isDocumentEntity(this.object.entityType) ? EsslObjectType.DOCUMENT : EsslObjectType.FILE;

    if (category) {
      this.apiUserCategoryAssignmentService.userCategoryAssignmentAssign({
        body: {
          categoryId: category.id!,
          entityType,
          entityId,
        }
      }).subscribe(categoryAssignment => {
        if (categoryAssignment) {
          this.userCategoryAssignment = categoryAssignment;
          this.cd.detectChanges();
        }
      });
    } else {
      this.apiUserCategoryAssignmentService.userCategoryAssignmentUnassign({
        entityType,
        entityId,
      }).subscribe(_ => {
        this.userCategoryAssignment = null;
        this.cd.detectChanges();
      });
    }
  }

  ngOnInit() {
    this.codebookService.issdApplications().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      issdApps => (this.issdApplications = issdApps)
    );

    this.documentFileDetailService.object$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(object => {
      this.object = object!;
      this.cd.detectChanges();
      this.apiUserCategoryAssignmentService.userCategoryAssignmentGetByEntity({
        entityId: this.object.id!,
        entityType: isDocumentEntity(this.object.entityType) ? EsslObjectType.DOCUMENT : EsslObjectType.FILE,
      }).subscribe(categoryAssignment => {
        if (categoryAssignment) {
          this.userCategoryAssignment = categoryAssignment;
          this.cd.detectChanges();
        }
      });
    });
  }

}
